import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// RichTextEditor component that replaces a textarea for jobDescription.
// Props:
// - name: string (form field name)
// - value: string (HTML string)
// - onChange: function(eventLike) where eventLike = { target: { name, value } }
// - placeholder: string
// - maxLength: number (defaults to 500)
// - required, disabled

export default function RichTextEditor({
  name = "jobDescription",
  value = "",
  onChange = () => {},
  placeholder = "Enter Job Description",
  maxLength = 1500,
  required = false,
  disabled = false,
}) {
  const [html, setHtml] = useState(value);
  const [plainLength, setPlainLength] = useState(0);
  const quillRef = useRef(null);

  useEffect(() => {
    setHtml(value);
    setPlainLength(countPlainText(value));
  }, [value]);

  function countPlainText(htmlString) {
    if (!htmlString) return 0;
    const tmp = document.createElement("div");
    tmp.innerHTML = htmlString;
    return tmp.textContent.trim().length;
  }

  function handleChange(newHtml) {
    const len = countPlainText(newHtml);
    if (len > maxLength) {
      const tmp = document.createElement("div");
      tmp.innerHTML = newHtml;
      const sliced = tmp.textContent.trim().slice(0, maxLength);
      const limited = `<p>${escapeHtml(sliced)}</p>`;
      setHtml(limited);
      setPlainLength(maxLength);
      onChange({ target: { name, value: limited } });
      return;
    }

    setHtml(newHtml);
    setPlainLength(len);
    onChange({ target: { name, value: newHtml } });
  }

  function escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "code-block"],
      ["link"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "blockquote",
    "code-block",
    "link",
  ];

  return (
    <div className="col-md-12 mt-3 mb-3">
      <label className="small mb-1" htmlFor={`rte-${name}`}>
        Description
        <span
          style={{ color: "red", paddingLeft: 4 }}
          className="text-red-700 pl-[2px]"
        >
          {required ? "*" : ""}
        </span>
      </label>

      <div className="border rounded mb-2 ">
        <ReactQuill
          ref={quillRef}
          id={`rte-${name}`}
          theme="snow"
          value={html}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
          style={{
            fontSize: "22px",
            fontFamily: "Poppins, sans-serif",
            lineHeight: "1.6",
            minHeight: "150px",
            height: "250px",
            paddingBottom: "15px",
            marginBottom: "10px",
            overflowX: "hidden",
            overflowY: "visible",
          }}
        />
      </div>

      <div className="flex justify-between items-center text-sm text-gray-600 mt-1">
        <div></div>
        <div style={{ paddingLeft: "2px" }}>
          {plainLength}/{maxLength} characters
          <hr />
        </div>
      </div>
    </div>
  );
}
